import { nextTick, onUnmounted, watch } from 'vue';
import type { Ref } from 'vue';
import { useAutopilotStore } from '@llmor/autopilot-vue';
import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';
import {
  CONTACT_METHOD_FIELD,
  describeTemplateFields,
  setFieldsJsonSchemaProperties,
  type PublicationFieldCatalogEntry,
} from '@/autopilot/functions/publications/describeTemplateFields';
import { publicationWorkflowGuide } from '@/autopilot/functions/publications/publicationWorkflow';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import type { FormQuestionInput, IFormQuestion } from '@/types/formQuestion';
import type { PublicationType } from '@/types/publication';
import type { TemplateMeta } from '@/types/templateMeta';

export type QuestionMutationResult =
  | { success: true; question?: IFormQuestion; questions?: IFormQuestion[] }
  | { success: false; error?: string; errors?: Record<string, string[]> };

const CONTACT_METHODS = ['email', 'phone', 'both'] as const;
type ContactMethod = (typeof CONTACT_METHODS)[number];

const QUESTION_TYPES = ['text', 'textarea', 'radio', 'select', 'checkbox', 'toggle'] as const;

export interface AutopilotPublicationEditorConfig {
  publication: Ref<PublicationType | undefined>;
  templateOptions: Ref<Record<string, unknown>>;
  templateMeta: Ref<TemplateMeta>;
  currentTab: Ref<'questions' | 'form' | 'settings'>;
  popsMode: Ref<'editor' | 'preview'>;
  formQuestions: Ref<IFormQuestion[]>;
  questionsLoading: Ref<boolean>;
  publicationSavingState: Ref<'idle' | 'typing' | 'saving' | 'saved' | 'error'>;
  isOptimizingContent: Ref<boolean>;
  optimizingPhase: Ref<'transform_content'>;
  companyImages: Ref<Array<{ hash: string; url: string }> | null>;
  cancelPendingSave: () => void;
  patchOption: (key: string, value: unknown) => void;
  reloadPreview: () => void;
  persist: () => Promise<boolean>;
  updateContactMethod: (value: string) => Promise<void>;
  expandSectionForKey: (key: string) => void;
  expandFormSection: (section: 'contact') => void;
  createQuestion: (input: FormQuestionInput) => Promise<QuestionMutationResult>;
  updateQuestion: (id: number, input: FormQuestionInput) => Promise<QuestionMutationResult>;
  deleteQuestion: (id: number) => Promise<QuestionMutationResult>;
  reorderQuestions: (ids: number[]) => Promise<QuestionMutationResult>;
}

function isContactMethod(value: unknown): value is ContactMethod {
  return typeof value === 'string' && (CONTACT_METHODS as readonly string[]).includes(value);
}

function compactQuestion(question: IFormQuestion) {
  return {
    id: question.id,
    type: question.type,
    name: question.name,
    label: question.label,
    description: question.description,
    required: question.required,
    options: question.options,
    sort_order: question.sort_order,
  };
}

function coerceImageValue(value: unknown): unknown {
  if (typeof value === 'string') return { hash: value };
  return value;
}

function catalogByKey(fields: PublicationFieldCatalogEntry[]): Map<string, PublicationFieldCatalogEntry> {
  return new Map(fields.map((field) => [field.key, field]));
}

function waitWhileSaving(state: Ref<string>): Promise<void> {
  if (state.value !== 'saving') return Promise.resolve();
  return new Promise((resolve) => {
    const stop = watch(state, (next) => {
      if (next !== 'saving') {
        stop();
        resolve();
      }
    });
  });
}

const questionProperties = {
  type: {
    type: 'string',
    enum: [...QUESTION_TYPES],
    description: 'Question input type.',
  },
  name: { type: 'string', description: 'Internal name of the question.' },
  label: { type: 'string', description: 'Label shown to the candidate.' },
  description: { type: 'string', description: 'Optional longer prompt shown to the candidate.' },
  hint: { type: 'string', description: 'Optional hint text.' },
  placeholder: { type: 'string', description: 'Optional placeholder.' },
  required: { type: 'boolean', description: 'Whether the candidate must answer. Defaults to true in the UI.' },
  options: {
    type: 'object',
    description: 'Type-specific options. For radio/select/checkbox, pass { choices: [{ label, value }] }.',
    properties: {
      choices: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            label: { type: 'string' },
            value: { type: 'string' },
          },
          required: ['label', 'value'],
        },
      },
    },
  },
  group: { type: 'string', description: 'Optional group name.' },
};

/**
 * Exposes the publication editor to autopilot: publishes `current_publication` + `publication_workflow`
 * and registers set_fields / set_tab / set_mode / question CRUD. Functions are registered during
 * setup so a deep-link route-readiness `ready()` already sees them.
 */
export function useAutopilotPublicationEditor(cfg: AutopilotPublicationEditorConfig): void {
  const store = useAutopilotStore();
  const functionNames: string[] = [];

  useAutopilotContext('publication_workflow', () => publicationWorkflowGuide);

  const withResolution = async <T>(label: string, work: () => Promise<T> | T): Promise<T> => {
    const release = store.client.beginContextResolution(label);
    try {
      return await work();
    } finally {
      await nextTick();
      release();
    }
  };

  const catalog = () => describeTemplateFields(cfg.templateMeta.value?.options);

  const persistNow = async (): Promise<boolean> => {
    cfg.cancelPendingSave();
    await waitWhileSaving(cfg.publicationSavingState);
    let saved = await cfg.persist();
    if (saved) return true;
    if (cfg.publicationSavingState.value === 'saving') {
      await waitWhileSaving(cfg.publicationSavingState);
      saved = await cfg.persist();
    }
    return saved;
  };

  const SET_FIELDS_NAME = 'current_publication_set_fields';

  const setFieldsHandler = async (rawArgs: unknown) => {
    const patch = (rawArgs ?? {}) as Record<string, unknown>;
    return withResolution(SET_FIELDS_NAME, async () => {
      const keys = Object.keys(patch);
      if (keys.length === 0) return { success: true, fields: [] };

      const fields = catalogByKey(catalog());
      const formKeys = new Set(Object.keys(cfg.templateOptions.value ?? {}));
      const unknown = keys.filter((key) => key !== CONTACT_METHOD_FIELD && !fields.has(key) && !formKeys.has(key));
      if (unknown.length) {
        return { success: false, error: 'Unknown field(s).', unknown };
      }

      if (CONTACT_METHOD_FIELD in patch) {
        if (!isContactMethod(patch[CONTACT_METHOD_FIELD])) {
          return { success: false, error: 'form_contact_method must be email, phone, or both.' };
        }
      }

      const applied: string[] = [];
      let forcesRefresh = false;
      let lastTab: 'form' | 'settings' | null = null;

      for (const key of keys) {
        if (key === CONTACT_METHOD_FIELD) continue;
        const field = fields.get(key);
        let value = patch[key];
        if (field?.input_type === 'input.image') value = coerceImageValue(value);
        cfg.templateOptions.value = { ...cfg.templateOptions.value, [key]: value };
        cfg.patchOption(key, value);
        if (field) {
          cfg.expandSectionForKey(key);
          lastTab = field.tab;
          if (field.tab === 'form') cfg.expandFormSection('contact');
          if (field.forces_refresh) forcesRefresh = true;
        }
        applied.push(key);
      }

      if (forcesRefresh) cfg.reloadPreview();
      if (lastTab) cfg.currentTab.value = lastTab;

      if (CONTACT_METHOD_FIELD in patch) {
        cfg.currentTab.value = 'form';
        cfg.expandFormSection('contact');
        await cfg.updateContactMethod(patch[CONTACT_METHOD_FIELD] as string);
        applied.push(CONTACT_METHOD_FIELD);
      }

      if (applied.some((key) => key !== CONTACT_METHOD_FIELD)) {
        const saved = await persistNow();
        if (!saved) return { success: false, error: 'Failed to persist publication.', fields: applied };
      }

      return { success: true, fields: applied };
    });
  };

  function buildSetFieldsDef(): AutopilotFunctionDefinition {
    return {
      name: SET_FIELDS_NAME,
      description:
        'Set one or more fields on the publication currently in the editor and persist them immediately. ' +
        'Every key in context.current_publication.form is writable (job title, body, lists, keywords, …) — ' +
        'that is the live job-ad content, including copy edited in the preview. ' +
        'context.current_publication.fields describes types/labels. Also accepts form_contact_method. ' +
        'There is no separate save; this call resolves once the saved values are in context.',
      parameters: {
        type: 'object',
        properties: setFieldsJsonSchemaProperties(catalog(), cfg.templateOptions.value),
        additionalProperties: true,
      },
      handler: setFieldsHandler,
    };
  }

  function registerSetFields() {
    store.client.unregisterFunction(SET_FIELDS_NAME);
    store.client.registerFunction(buildSetFieldsDef());
  }

  const setTab: AutopilotFunctionDefinition = {
    name: 'current_publication_set_tab',
    description: 'Switch the publication editor between the questions, form and settings tabs.',
    parameters: {
      type: 'object',
      properties: {
        tab: {
          type: 'string',
          enum: ['questions', 'form', 'settings'],
          description: 'Tab to show.',
        },
      },
      required: ['tab'],
    },
    handler: async (rawArgs) => {
      const tab = (rawArgs as { tab?: string } | null)?.tab;
      return withResolution('current_publication_set_tab', () => {
        if (tab !== 'questions' && tab !== 'form' && tab !== 'settings') {
          return { success: false, error: 'tab must be questions, form or settings.' };
        }
        cfg.currentTab.value = tab;
        return { success: true, tab };
      });
    },
  };

  const setMode: AutopilotFunctionDefinition = {
    name: 'current_publication_set_mode',
    description: 'Switch the publication preview between editor and preview mode.',
    parameters: {
      type: 'object',
      properties: {
        mode: {
          type: 'string',
          enum: ['editor', 'preview'],
          description: 'editor for in-place content edits, preview for the candidate-facing view.',
        },
      },
      required: ['mode'],
    },
    handler: async (rawArgs) => {
      const mode = (rawArgs as { mode?: string } | null)?.mode;
      return withResolution('current_publication_set_mode', () => {
        if (mode !== 'editor' && mode !== 'preview') {
          return { success: false, error: 'mode must be editor or preview.' };
        }
        cfg.popsMode.value = mode;
        return { success: true, mode };
      });
    },
  };

  const addQuestion: AutopilotFunctionDefinition = {
    name: 'current_publication_add_question',
    description:
      'Create a form question on the current publication. The new question is appended and persisted. ' +
      'Read the resulting list from context.current_publication.questions.',
    parameters: {
      type: 'object',
      properties: questionProperties,
      required: ['type', 'name'],
    },
    handler: async (rawArgs) => {
      const input = (rawArgs ?? {}) as FormQuestionInput;
      return withResolution('current_publication_add_question', async () => {
        cfg.currentTab.value = 'questions';
        return cfg.createQuestion(input);
      });
    },
  };

  const updateQuestion: AutopilotFunctionDefinition = {
    name: 'current_publication_update_question',
    description: 'Update an existing form question by id. Persists immediately.',
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'integer', description: 'Question id from context.current_publication.questions.' },
        ...questionProperties,
      },
      required: ['id', 'type', 'name'],
    },
    handler: async (rawArgs) => {
      const { id, ...input } = (rawArgs ?? {}) as FormQuestionInput & { id?: number };
      return withResolution('current_publication_update_question', async () => {
        if (typeof id !== 'number') return { success: false, error: 'id is required.' };
        cfg.currentTab.value = 'questions';
        return cfg.updateQuestion(id, { ...input, type: input.type, name: input.name });
      });
    },
  };

  const deleteQuestion: AutopilotFunctionDefinition = {
    name: 'current_publication_delete_question',
    description: 'Delete a form question by id. Persists immediately.',
    sensitive: true,
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'integer', description: 'Question id from context.current_publication.questions.' },
      },
      required: ['id'],
    },
    handler: async (rawArgs) => {
      const id = (rawArgs as { id?: number } | null)?.id;
      return withResolution('current_publication_delete_question', async () => {
        if (typeof id !== 'number') return { success: false, error: 'id is required.' };
        cfg.currentTab.value = 'questions';
        return cfg.deleteQuestion(id);
      });
    },
  };

  const reorderQuestions: AutopilotFunctionDefinition = {
    name: 'current_publication_reorder_questions',
    description:
      'Reorder form questions. Pass the complete list of question ids in the new order. Persists immediately.',
    parameters: {
      type: 'object',
      properties: {
        ids: {
          type: 'array',
          items: { type: 'integer' },
          description: 'Every question id, in the desired order.',
        },
      },
      required: ['ids'],
    },
    handler: async (rawArgs) => {
      const ids = (rawArgs as { ids?: number[] } | null)?.ids;
      return withResolution('current_publication_reorder_questions', async () => {
        if (!Array.isArray(ids) || ids.length === 0) {
          return { success: false, error: 'ids must be a non-empty array of question ids.' };
        }
        cfg.currentTab.value = 'questions';
        return cfg.reorderQuestions(ids);
      });
    },
  };

  const functions = [setTab, setMode, addQuestion, updateQuestion, deleteQuestion, reorderQuestions];
  for (const def of functions) {
    store.client.registerFunction(def);
    functionNames.push(def.name);
  }

  // re-register whenever the catalog or live form keys change so the JSON-schema actually lists
  // those properties — models will not pass undocumented keys even with additionalProperties: true
  registerSetFields();
  functionNames.push(SET_FIELDS_NAME);
  const stopSchemaWatch = watch(
    () => [
      cfg.templateMeta.value,
      Object.keys(cfg.templateOptions.value ?? {})
        .sort()
        .join('\0'),
    ],
    () => registerSetFields()
  );

  const stopWatch = watch(
    [
      cfg.publication,
      cfg.templateOptions,
      cfg.templateMeta,
      cfg.currentTab,
      cfg.popsMode,
      cfg.formQuestions,
      cfg.questionsLoading,
      cfg.publicationSavingState,
      cfg.isOptimizingContent,
      cfg.optimizingPhase,
      cfg.companyImages,
    ],
    () => {
      const publication = cfg.publication.value;
      store.context.current_publication = {
        description:
          'The publication editor currently on screen. Drive it with current_publication_set_fields and the question tools; field types are in fields.',
        pubkey: publication?.pubkey,
        name: publication?.name,
        template: publication?.template,
        variation: publication?.variation ?? null,
        funnel_enabled: !!publication?.funnel_enabled,
        public_url: publication?.public_url,
        preview_url: publication?.preview_url,
        tab: cfg.currentTab.value,
        mode: cfg.popsMode.value,
        save_state: cfg.publicationSavingState.value,
        is_optimizing: cfg.isOptimizingContent.value,
        optimizing_phase: cfg.isOptimizingContent.value ? cfg.optimizingPhase.value : null,
        questions_loading: cfg.questionsLoading.value,
        contact_method: publication?.form_contact_method ?? null,
        fields: catalog(),
        form: { ...cfg.templateOptions.value },
        company_images: cfg.companyImages.value,
        questions: (cfg.formQuestions.value ?? []).map(compactQuestion),
      };
    },
    { immediate: true, deep: true }
  );

  onUnmounted(() => {
    stopWatch();
    stopSchemaWatch();
    for (const name of functionNames) store.client.unregisterFunction(name);
    delete store.context.current_publication;
  });
}
